import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  getFullApiUrl,
  ITEMS_PER_PAGE,
  podcastId,
  podcast as podcastConfig,
} from "../../config";
import RssTextField from "./RssTextField";
import {
  extractVol,
  formatDateSlash,
  getPodcastSession,
  truncateContent,
} from "../../utils";
import { durationToSeconds, toPlayerEpisode, usePlayer } from "../../player/PlayerContext";
import { EpisodeRow, Input, ShowHeader } from "../../designSystem";
import Pager from "../ui/Pager";
import { useMembership } from "../../hooks/useMembership";
import { LoadingPage } from "../LoadingPage";
import { ErrorPage } from "../ErrorPage";

const PODCAST_ID = podcastId;

/** 全量单集数据的会话级缓存：整个 SPA 会话只请求一次，
    分页与搜索都在本地完成，不再产生额外请求。 */
let podcastDataCache = null;

/** HTML 转纯文本（用于本地全文搜索）。 */
function htmlToPlainText(html) {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.textContent || "";
}

const PodcastBlog = () => {
  const [podcastData, setPodcastData] = useState(podcastDataCache);
  const [loading, setLoading] = useState(!podcastDataCache);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");

  const navigate = useNavigate();
  const player = usePlayer();
  // 会员态以服务端付费状态为准（会员过期后本地 session 仍存在，不可作为依据）
  const { isMember } = useMembership();

  useEffect(() => {
    if (podcastDataCache) return;
    const fetchPodcastData = async () => {
      try {
        setLoading(true);
        // 一次性拉取全部单集，之后分页/搜索均在本地完成
        const response = await axios.get(
          getFullApiUrl("/api/v1/subscribe/get_podcast_in_json"),
          {
            params: { start: 0, end: 100000, podcast: PODCAST_ID },
          }
        );
        podcastDataCache = response.data;
        setPodcastData(response.data);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching podcast data:", err);
        setError("节目列表加载失败，请稍后重试。");
        setLoading(false);
      }
    };

    fetchPodcastData();
  }, []);

  const parsed = useMemo(() => {
    if (!podcastData) return null;
    const episodes = JSON.parse(podcastData.episodes);
    return {
      podcast: JSON.parse(podcastData.podcast),
      episodes,
      // 预生成小写纯文本索引，供本地全文搜索
      searchIndex: episodes.map((e) =>
        `${e.Title} ${htmlToPlainText(e.Content)}`.toLowerCase()
      ),
      total: podcastData.number_of_episodes,
    };
  }, [podcastData]);

  if (loading) {
    return <LoadingPage />;
  }

  if (error) {
    return <ErrorPage error={error} />;
  }

  const { podcast, episodes, searchIndex, total } = parsed;
  const podcastSession = getPodcastSession() || "";

  const query = searchQuery.trim().toLowerCase();
  const isSearchMode = query !== "";
  const matchedEpisodes = isSearchMode
    ? episodes.filter((_, i) => searchIndex[i].includes(query))
    : episodes;
  const totalPages = Math.ceil(matchedEpisodes.length / ITEMS_PER_PAGE);
  const listEpisodes = isSearchMode
    ? matchedEpisodes
    : matchedEpisodes.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE);

  const isLocked = (episode) => episode.IsPaid && !isMember;

  const handlePlay = (episode) => {
    if (isLocked(episode)) {
      navigate("/membership-intro");
      return;
    }
    player.play(toPlayerEpisode(episode, podcastSession));
  };

  const handleQueue = (episode) => {
    player.queueAdd(toPlayerEpisode(episode, podcastSession));
  };

  const handleOpen = (episodeId) => {
    const searchParams = podcastSession
      ? `?podcast_session=${podcastSession}`
      : "";
    navigate(`/episode/${episodeId}${searchParams}`);
  };

  const playLatest = () => {
    const first = episodes.find((e) => !isLocked(e)) || episodes[0];
    if (first) handlePlay(first);
  };

  return (
    <>
      <div className="lt-hero-band">
        <div className="lt-main">
          <ShowHeader
            title={podcast.Title || podcastConfig.Title}
            cover={`${process.env.PUBLIC_URL}/logo512.png`}
            tagline={podcast.Description || podcastConfig.Description}
            isMember={isMember}
            onPlayLatest={playLatest}
            onMembership={() => navigate("/membership-intro")}
          />
        </div>
      </div>
      <div className="lt-main">
        <div className="lt-section">
          <div className="lt-section-head">
            <h2>订阅收听</h2>
          </div>
          <p style={{ color: "var(--text-3)", fontSize: "var(--text-sm)", margin: "0 0 10px" }}>
            {isMember
              ? "这是您的专属 RSS 链接，粘贴到任意泛用型播客客户端即可收听全部单集。"
              : "将 RSS 链接粘贴到任意泛用型播客客户端即可收听公开单集。"}
          </p>
          <RssTextField podcastSession={podcastSession || null} />
        </div>

        <div className="lt-section">
          <div className="lt-section-head lt-section-head--search">
            <h2>{isSearchMode ? "搜索结果" : "全部单集"}</h2>
            <div className="lt-search">
              <Input
                icon="search"
                placeholder="搜索单集"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-label="搜索单集"
              />
              <span
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: "var(--text-sm)",
                  color: "var(--text-3)",
                  whiteSpace: "nowrap",
                }}
              >
                {isSearchMode ? `${matchedEpisodes.length} 个结果` : `共 ${total} 集`}
              </span>
            </div>
          </div>
          <div className="lt-list">
            {listEpisodes.map((episode) => (
              <EpisodeRow
                key={episode.ID}
                episode={{
                  id: episode.ID,
                  vol: extractVol(episode.Title),
                  cover: `${process.env.PUBLIC_URL}/logo192.png`,
                  title: episode.Title,
                  desc: truncateContent(episode.Content || ""),
                  date: formatDateSlash(episode.PublishedAt),
                  durationMin: Math.max(
                    1,
                    Math.round(durationToSeconds(episode.Duration) / 60)
                  ),
                  memberOnly: !!episode.IsPaid,
                }}
                current={player.episode?.id === episode.ID}
                playing={player.episode?.id === episode.ID && player.playing}
                locked={isLocked(episode)}
                onPlay={() => handlePlay(episode)}
                onOpen={() => handleOpen(episode.ID)}
                onQueue={() => handleQueue(episode)}
              />
            ))}
          </div>
          {isSearchMode && matchedEpisodes.length === 0 ? (
            <div className="lt-center-state" style={{ minHeight: "20vh" }}>
              <p>没有找到与「{searchQuery.trim()}」相关的单集。</p>
            </div>
          ) : null}
          {!isSearchMode ? (
            <Pager page={page} totalPages={totalPages} onChange={setPage} />
          ) : null}
        </div>
      </div>
    </>
  );
};

export default PodcastBlog;
